import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import lifecycleArrowRightIcon from '@/assets/quotes/life-of-quote/arrow-right.svg'
import lifecycleCheckIcon from '@/assets/quotes/life-of-quote/check.svg'
import lifecycleLoaderIcon from '@/assets/quotes/life-of-quote/loader.svg'

type InvoiceLifecycleStepState = 'complete' | 'active' | 'upcoming' | 'neutral'

export type InvoiceLifecycleStep = {
  key: 'created' | 'validated' | 'sent' | 'paid'
  label: string
  detail?: string | string[] | null
  state: InvoiceLifecycleStepState
  onClick?: () => void
}

const sectionCardClass =
  'flex min-w-[240px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)]'

const isPresentString = (value: string | null | undefined): value is string =>
  Boolean(value)

export function InvoiceLifecycleCard({
  steps,
  neutralMessage,
  onHistoryClick,
}: {
  steps: InvoiceLifecycleStep[]
  neutralMessage?: string | null
  onHistoryClick: () => void
}) {
  const { t } = useTranslation('invoices')

  return (
    <section className={sectionCardClass}>
      <div className='flex h-16 items-center gap-2 px-6 pb-2 pt-6'>
        <div className='flex min-w-0 flex-1 items-center gap-3'>
          <span className='flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#ecfdf5] p-2 text-[#059669]'>
            <img
              src={lifecycleLoaderIcon}
              alt=''
              aria-hidden
              className='size-4'
            />
          </span>
          <h2 className='font-heading truncate text-sm font-semibold leading-[1.2] text-[#171a20]'>
            {t('detail.lifecycle.title')}
          </h2>
        </div>
      </div>

      <div className='flex flex-col gap-2 px-6 pb-6 pt-4'>
        <div className='-mx-6 overflow-x-auto px-6 pb-1'>
          <div className='min-w-[760px]'>
            <div className='relative h-6'>
              {steps.slice(0, -1).map((step, index) => {
                const next = steps[index + 1]
                const connectorComplete =
                  step.state === 'complete' && next.state !== 'neutral'

                return (
                  <div
                    key={`${step.key}-connector`}
                    className={cn(
                      'absolute top-[11px] h-0.5',
                      connectorComplete ? 'bg-[#10b981]' : 'bg-[#e7e9ef]',
                      step.state === 'neutral' && 'bg-[#e7e9ef]'
                    )}
                    style={{
                      left: `calc(${12.5 + index * 25}% + 20px)`,
                      width: 'calc(25% - 40px)',
                    }}
                  />
                )
              })}
              {steps[3]?.state === 'active' && (
                <div
                  className='absolute right-[-24px] top-[11px] h-0.5 bg-[#e7e9ef]'
                  style={{ left: 'calc(87.5% + 20px)' }}
                />
              )}
              <div className='relative grid grid-cols-4'>
                {steps.map(step => {
                  const isComplete = step.state === 'complete'
                  const isActive = step.state === 'active'
                  const isNeutral = step.state === 'neutral'

                  return (
                    <div
                      key={`${step.key}-marker`}
                      className='flex justify-center'
                    >
                      <div
                        className={cn(
                          'flex size-6 shrink-0 items-center justify-center rounded-full',
                          isComplete && 'bg-[#10b981] text-white',
                          isActive &&
                            'border-[6px] border-[#ff5a1f] bg-white text-[#ff5a1f]',
                          (step.state === 'upcoming' || isNeutral) &&
                            'border border-[#b0b5c3] bg-white'
                        )}
                      >
                        {isComplete && (
                          <img
                            src={lifecycleCheckIcon}
                            alt=''
                            aria-hidden
                            className='size-3.5'
                          />
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className='mt-3 grid grid-cols-4 items-start'>
              {steps.map(step => {
                const details: string[] = Array.isArray(step.detail)
                  ? step.detail.filter(isPresentString)
                  : step.detail
                    ? [step.detail]
                    : []
                const isNeutral = step.state === 'neutral'

                return (
                  <div
                    key={step.key}
                    className='flex min-w-0 flex-col items-center text-center'
                  >
                    <div className='flex min-w-0 flex-col items-center gap-1'>
                      {step.onClick ? (
                        <button
                          type='button'
                          className='inline-flex items-center gap-1 text-sm font-medium leading-[1.4] text-[#ff5a1f]'
                          onClick={step.onClick}
                        >
                          <span className='truncate'>{step.label}</span>
                          <img
                            src={lifecycleArrowRightIcon}
                            alt=''
                            aria-hidden
                            className='size-4 shrink-0'
                          />
                        </button>
                      ) : (
                        <p
                          className={cn(
                            'text-sm font-medium leading-[1.4]',
                            isNeutral ? 'text-[#767676]' : 'text-[#171a20]',
                            step.state === 'upcoming' && 'text-[#3d434e]'
                          )}
                        >
                          {step.label}
                        </p>
                      )}
                      {details.map(detail => (
                        <p
                          key={detail}
                          className='max-w-full truncate text-xs font-medium leading-4 text-[#3d434e]'
                        >
                          {detail}
                        </p>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {neutralMessage && (
          <p className='text-sm leading-[1.4] text-[#767676]'>
            {neutralMessage}
          </p>
        )}

        <button
          type='button'
          className='inline-flex w-fit items-center gap-1 text-xs font-medium leading-4 text-[#767676] hover:text-[#171a20]'
          onClick={onHistoryClick}
        >
          {t('detail.lifecycle.allHistory')}
          <ArrowRight className='size-4' />
        </button>
      </div>
    </section>
  )
}
